// App Success Stories - Real project results and case studies
import React, { useState } from 'react';
import { TrendingUp, Clock, Smartphone, ArrowRight, DollarSign, Users, Zap, Brain, CheckCircle } from 'lucide-react';
import { useScrollReveal } from '@/hooks/use-scroll-reveal';
import { MicroLabel } from './micro-label';
const successStories = [{
  id: 'propertyagent-crm',
  title: 'PropertyAgent-CRM',
  subtitle: 'AI-Powered Real Estate CRM for Professional Agents',
  description: 'The best CRM for real estate agents with advanced lead management, deal tracking, and commission forecasting. Built for solo agents and real estate professionals.',
  timeline: '12 weeks',
  category: 'Web Application + PWA',
  results: [{
    metric: '+30%',
    label: 'Closing rate increase',
    icon: TrendingUp
  }, {
    metric: 'Mobile-First',
    label: 'PWA for field work',
    icon: Smartphone
  }, {
    metric: 'AI Copilot',
    label: 'Pipeline risk alerts',
    icon: Brain
  }, {
    metric: 'Visual',
    label: 'Kanban deal pipeline',
    icon: CheckCircle
  }],
  techStack: ['React', 'TypeScript', 'Supabase', 'Tailwind CSS'],
  challenge: 'Real estate agents needed a CRM specifically designed for their workflow - not a generic system adapted for real estate. They needed mobile access for field work, visual pipeline management, and AI-powered insights.',
  solution: 'Built a mobile-first PWA with AI-powered pipeline analysis, visual Kanban-style deal tracking, commission forecasting, and real-time analytics designed by real estate professionals.',
  keyFeatures: ['AI-powered pipeline analysis with at-risk deal alerts', 'Visual Kanban deal pipeline with drag-and-drop', 'Real-time commission forecasting and revenue tracking', 'Mobile-first PWA architecture for field work', 'Complete lead-to-closing workflow management'],
  image: '/images/portfolio/propertyagent-dashboard.png',
  logo: '/images/portfolio/propertyagent-logo.png'
}, {
  id: 'selgr',
  title: 'SELGR',
  subtitle: 'AI-Powered Sales Training for Norwegian B2B Salespeople',
  description: 'A practical digital training platform that helps salespeople become more confident and effective in customer meetings through realistic AI roleplay, personalized feedback, and structured course modules.',
  timeline: '16 weeks',
  category: 'SaaS Platform',
  results: [{
    metric: 'AI Roleplay',
    label: 'Realistic customer simulations',
    icon: Brain
  }, {
    metric: 'Personalized',
    label: 'Adapts to your product/company',
    icon: Users
  }, {
    metric: '10+ Modules',
    label: 'Structured course content',
    icon: CheckCircle
  }, {
    metric: 'Real-time',
    label: 'Live coaching feedback',
    icon: Zap
  }],
  techStack: ['React', 'Node.js', 'PostgreSQL', 'OpenAI', 'Gemini', 'Auth0', 'Stripe'],
  challenge: 'Norwegian B2B salespeople needed practical, hands-on training - not theoretical courses. They required a way to practice customer conversations safely, get instant feedback, and track their progress over time.',
  solution: 'Built a comprehensive sales training SaaS with AI-powered roleplay simulations that adapt to each user\'s company, product, and skill level. Includes structured course modules with quizzes, practical daily tasks, progression tracking with badges, and real-time coaching during practice sessions.',
  keyFeatures: ['AI roleplay with realistic customer personas and objection handling', 'Live coaching and feedback during practice conversations', 'Course modules with lessons, examples, and interactive quizzes', 'Customization to user\'s specific product, service, and company', 'Progression tracking with XP rewards and achievement badges', 'Multiple difficulty levels from beginner to advanced'],
  image: '/images/portfolio/selgr-roleplay.png',
  logo: '/images/portfolio/selgr-logo.png'
}];
export const AppSuccessStories = () => {
  const [activeStory, setActiveStory] = useState(0);
  const {
    elementRef,
    getRevealStyle
  } = useScrollReveal();
  const currentStory = successStories[activeStory];
  return <section className="section">
      <div ref={elementRef} className="container-professional">
        {/* Header */}
        <div className="section-header-professional">
          <div className="micro-label-professional">SUCCESS STORIES</div>
          <h2 className="heading-section">
            Real projects, measurable results
          </h2>
          <p className="text-body max-w-2xl mx-auto" style={{
          color: 'rgba(255, 255, 255, 0.7)'
        }}>
            Discover how we've helped businesses streamline operations and achieve growth goals
          </p>
        </div>

        {/* Story navigation tabs */}
        <div className="flex flex-wrap justify-center gap-3 mb-12 max-w-4xl mx-auto">
          {successStories.map((story, index) => <button key={story.id} onClick={() => setActiveStory(index)} className="px-6 py-3 rounded-lg text-sm font-semibold transition-all duration-200" style={{
          background: activeStory === index ? '#00e5ff' : '#111111',
          color: activeStory === index ? '#FFFFFF' : 'rgba(255, 255, 255, 0.7)',
          border: `1px solid ${activeStory === index ? '#00e5ff' : 'rgba(255, 255, 255, 0.15)'}`
        }}>
              <div>{story.title}</div>
              <div className="text-xs opacity-70 mt-1">{story.category}</div>
            </button>)}
        </div>

        {/* Active story content */}
        <div className="bg-[#111111] border border-[rgba(255,255,255,0.15)] rounded-xl p-8 md:p-12 max-w-5xl mx-auto" style={getRevealStyle(1)}>
          {/* Logo and Title */}
          <div className="mb-8">
            <div className="flex items-center gap-4 mb-4">
              {currentStory.logo && <div className="bg-white rounded-lg p-3 flex-shrink-0">
                  <img src={currentStory.logo} alt={`${currentStory.title} logo`} className="h-8 w-auto" />
                </div>}
              <div className="flex items-center gap-3">
                <span className="px-3 py-1 rounded-full text-xs font-semibold" style={{
                background: 'rgba(0, 229, 255, 0.2)',
                color: '#00e5ff'
              }}>
                  {currentStory.category}
                </span>
                <span className="flex items-center gap-2 text-xs" style={{
                color: 'rgba(255, 255, 255, 0.6)'
              }}>
                  <Clock className="w-3.5 h-3.5" />
                  {currentStory.timeline}
                </span>
              </div>
            </div>
            
            <h3 className="text-2xl font-semibold text-white mb-3 leading-tight">
              {currentStory.title}
            </h3>
            
            <p className="text-base leading-relaxed" style={{
            color: 'rgba(255, 255, 255, 0.7)'
          }}>
              {currentStory.description}
            </p>
          </div>

          {/* Screenshot */}
          {currentStory.image && <div className="mb-8 -mx-8 md:-mx-12">
              <img src={currentStory.image} alt={`${currentStory.title} interface`} className="w-full h-auto rounded-lg border border-[rgba(255,255,255,0.1)]" />
            </div>}

          {/* Results metrics */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
            {currentStory.results.map((result, index) => {
            const IconComponent = result.icon;
            return <div key={index} className="text-center p-4 bg-[#0a0a0a] rounded-lg border border-[rgba(255,255,255,0.08)]">
                  <IconComponent className="w-6 h-6 mx-auto mb-2" strokeWidth={1.5} style={{
                color: '#00e5ff'
              }} />
                  <div className="text-xl font-bold text-white mb-1">{result.metric}</div>
                  <div className="text-xs" style={{
                color: 'rgba(255, 255, 255, 0.6)'
              }}>{result.label}</div>
                </div>;
          })}
          </div>

          {/* Project details */}
          <div className="grid lg:grid-cols-2 gap-8 mb-8">
            <div>
              <h4 className="text-sm font-semibold text-white mb-3">The Challenge</h4>
              <p className="text-sm leading-relaxed" style={{
              color: 'rgba(255, 255, 255, 0.7)'
            }}>
                {currentStory.challenge}
              </p>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-white mb-3">Our Solution</h4>
              <p className="text-sm leading-relaxed" style={{
              color: 'rgba(255, 255, 255, 0.7)'
            }}>
                {currentStory.solution}
              </p>
            </div>
          </div>

          {/* Key Features */}
          {currentStory.keyFeatures && <div className="mb-8">
              <h4 className="text-sm font-semibold text-white mb-4">Key Features Delivered</h4>
              <div className="grid sm:grid-cols-2 gap-3">
                {currentStory.keyFeatures.map((feature, index) => <div key={index} className="flex items-start gap-3 p-3 bg-[#0a0a0a] rounded-lg border border-[rgba(255,255,255,0.08)]">
                    <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" strokeWidth={1.5} style={{
                color: '#00e5ff'
              }} />
                    <span className="text-xs leading-relaxed" style={{
                color: 'rgba(255, 255, 255, 0.7)'
              }}>
                      {feature}
                    </span>
                  </div>)}
              </div>
            </div>}

          {/* Tech Stack */}
          <div className="mb-8">
            <h4 className="text-sm font-semibold text-white mb-4">Technology Stack</h4>
            <div className="flex flex-wrap gap-2">
              {currentStory.techStack.map((tech, index) => <span key={index} className="px-3 py-1.5 rounded-lg text-xs font-medium border" style={{
              background: 'rgba(255, 255, 255, 0.05)',
              color: 'rgba(255, 255, 255, 0.7)',
              borderColor: 'rgba(255, 255, 255, 0.08)'
            }}>
                  {tech}
                </span>)}
            </div>
          </div>

          {/* CTA */}
          
        </div>
      </div>
    </section>;
};